import { LocalStorage, navigation } from '@/config'
import { Medias } from '@/config/packages'
import { APIClient, ConnectID, firebase } from '@/services'
import { appStatusStore } from '@/stores'
import { queryClient } from '../queryClient'
import { waitFor } from '@codeleap/utils'
import { alert } from '@codeleap/modals'
import { t } from '@lingui/core/macro'
import { Profile } from '@/types'
import { requestPasswordReset } from './views'
import { QueryKeys } from './queryKeys'

const getThemeId = () => {
  const themeName = QueryKeys.me.getData()?.tid
  return themeName
}

export const userHasSocialLogin = async () => {
  const firebaseUser = firebase.auth.currentUser

  if (!firebaseUser?.uid) return { hasSocialLogin: false, signInMethods: [] }

  const idTokenResult = await firebaseUser.getIdTokenResult()

  if (idTokenResult?.claims?.linkedin_id) {
    return { hasSocialLogin: true, signInMethods: ['linkedIn'] }
  }

  const signInMethods = await firebase.auth.fetchSignInMethodsForEmail(firebaseUser?.email)

  const hasSocialLogin = signInMethods?.some(x => x !== 'password')

  return { hasSocialLogin, signInMethods }
}

export const logout = (withFeedback = true) => {
  return new Promise<void>(async (resolve, reject) => {
    if (withFeedback) {
      appStatusStore.set('loading')
    }

    await waitFor(1000)

    Medias.clearToken()
    LocalStorage.clear()
    queryClient.client.clear()
    ConnectID.ConnectIDAuth.instance.logout()
    appStatusStore.set('done')
    await APIClient.Session.unloadTheme()

    await waitFor(1000)

    if (withFeedback) {
      appStatusStore.set('done')
    }

    navigation.navigate('Home')

    await waitFor(500)

    resolve()
  })
}

export const handleLogout = () => {
  alert.ask({
    title: t`Logout`,
    body: t`Are you sure you want to log out of your account from this device?`,
    options: [
      {
        text: t`Cancel`,
        style: 'outline',
      },
      {
        onPress: logout,
        text: t`Logout`,
        style: 'destructive',
      },
    ],
  })
}

export const reauthenticate = (password: string, profile: Profile = null) => {
  const currentProfile = profile ?? QueryKeys.me.getData()
  return firebase.auth.signInWithEmailAndPassword(currentProfile?.email, password)
}

export const toggleRememberInformation = async (rememberMe: boolean) => {
  return firebase.auth.setPersistence(rememberMe ? { type: 'SESSION' } : { type: 'NONE' })
}

export const handleChangePassword = (email: string) => {
  const onRequestPasswordReset = async () => {
    try {
      appStatusStore.set('loading')
      await requestPasswordReset(email)
      appStatusStore.set('done')
    } catch (err) {
      appStatusStore.set('idle')
    }
  }

  alert.ask({
    title: t`Change password`,
    body: t`Are you sure you want to change your password?`,
    options: [
      {
        text: t`Proceed`,
        onPress: onRequestPasswordReset,
      },
      {
        text: t`Cancel`,
        style: 'outline',
      },
    ],
  })
}

export async function loadTheme(refresh = false) {
  const { CompanyTheme } = await import('../../../config/packages')

  try {
    const themeName = getThemeId()
    if (!themeName) return true

    const { reload } = await CompanyTheme.loadTheme(themeName, refresh)

    if (reload) {
      window.location.reload()
    }
    return !reload
  } catch (e) {
    logger.error(e)
    return true
  }

}

export async function unloadTheme() {
  const { CompanyTheme } = await import('../../../config/packages')

  try {
    await CompanyTheme.unload(getThemeId())

  } catch (e) {
    logger.error(e)
  }

}
